/**
 * Market-Hub System Capabilities & Function Tree SSOT
 * Standard: SPEC-MARKET_HUB-CAPABILITY-MAP-001
 * Source: docs/context/CAPABILITY_MAP.md
 */

export type CapabilityDomainId = 
  | 'ingress_storage' 
  | 'quant_indicators' 
  | 'ui_presentation' 
  | 'gateway_governance';

export type LifecycleStatus = 'STABLE' | 'EVOLVING' | 'PLANNED' | 'DEPRECATED';
export type ReuseBadge = 'SHARED' | 'DEDICATED' | 'OVERLAP_CANDIDATE';

export interface SystemCapability {
  id: string;
  name: string;
  domainId: CapabilityDomainId;
  domainNameZh: string;
  domainNameEn: string;
  valueOutcome: string;
  lifecycle: LifecycleStatus;
  reuse: ReuseBadge;
  pointerText: string;
  route?: string;
}

export interface CapabilityDomainMeta {
  id: CapabilityDomainId;
  nameZh: string;
  nameEn: string;
  icon: string;
  description: string;
}

export const CAPABILITY_DOMAINS: CapabilityDomainMeta[] = [
  {
    id: 'ingress_storage',
    nameZh: '跨市场时序行情与摄取域',
    nameEn: 'INGRESS & STORAGE DOMAIN',
    icon: '⚡',
    description: '纳秒级归一化多交易所行情流水线、A股双时段数据拉取与 TimescaleDB 分层持久化',
  },
  {
    id: 'quant_indicators',
    nameZh: '量化特征与指标计算域',
    nameEn: 'QUANT & INDICATORS DOMAIN',
    icon: '📐',
    description: '多周期重采样引擎、纯 Decimal 技术指标计算、趋势气象评分与主力资金画像',
  },
  {
    id: 'ui_presentation',
    nameZh: '全景交互与表现呈现域',
    nameEn: 'UI & PRESENTATION DOMAIN',
    icon: '🖥️',
    description: '加密多空看板、A股 8 大专项分析大屏、双语排版系统与 5 态真值机表现层',
  },
  {
    id: 'gateway_governance',
    nameZh: '网关安全与系统治理底座',
    nameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    icon: '🛡️',
    description: 'BFF 动态 HMAC 验签网关、异步 Mailbox 协议通道、全景接地与 ASOP 门禁',
  },
];

export const SYSTEM_CAPABILITIES: SystemCapability[] = [
  // 1. Ingress & Storage
  {
    id: 'cap-crypto-ingress',
    name: '加密多交易所实时采集',
    domainId: 'ingress_storage',
    domainNameZh: '跨市场时序行情与摄取域',
    domainNameEn: 'INGRESS & STORAGE DOMAIN',
    valueOutcome: '纳秒级归一化 Binance/OKX/Bybit 行情流，严格因果时延 W>=3s',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ownership-table, CanonicalCandle',
  },
  {
    id: 'cap-ashare-ingress',
    name: 'A股分时与日线摄取流',
    domainId: 'ingress_storage',
    domainNameZh: '跨市场时序行情与摄取域',
    domainNameEn: 'INGRESS & STORAGE DOMAIN',
    valueOutcome: '支持 5500+ A 股日线/分时双交易时段拉取与收盘快照同步',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ownership-table, Stage 040/047',
  },
  {
    id: 'cap-timescale-tiering',
    name: '时序持久化存储与分层',
    domainId: 'ingress_storage',
    domainNameZh: '跨市场时序行情与摄取域',
    domainNameEn: 'INGRESS & STORAGE DOMAIN',
    valueOutcome: 'TimescaleDB 分层持久化与 Hypertable 分区，100% Pure Decimal',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'ADR-069, SPEC-QUANT-DATA-TIERING-L0-L1-L2',
  },
  {
    id: 'cap-provider-pool',
    name: '多源数据提供商熔断切换',
    domainId: 'ingress_storage',
    domainNameZh: '跨市场时序行情与摄取域',
    domainNameEn: 'INGRESS & STORAGE DOMAIN',
    valueOutcome: '上游故障时自动平滑故障转移与无缝健康探测',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ownership-table, ProviderPool',
  },

  // 2. Quant & Indicators
  {
    id: 'cap-resampler-engine',
    name: '多周期 Resampler 引擎',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '1m 基础 K 线无锁重采样生成 5m/15m/1h/4h/1d 多周期 Bar',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ownership-table, ResamplerZeroFloat',
  },
  {
    id: 'cap-indicators-engine',
    name: '核心技术指标计算引擎',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: 'EMA, RSI, MACD, ATR, 布林带及斐波那契支撑阻力位实时计算',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ownership-table, PureDecimalAST',
  },
  {
    id: 'cap-trend-weather-ssot',
    name: '市场趋势气象评估 (Weather SSOT)',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '评估市场多空态势、时空共振得分与多空微气象热力图',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'PROPOSAL_STAGE_046, TrendWeatherSSOT',
  },
  {
    id: 'cap-regime-semantics',
    name: '市场态势语义与回测中枢',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '识别盘整/单边等 6 类市场政权状态，并支持轻量回测推演',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'ADR-052, RegimeSemanticsEngine',
  },
  {
    id: 'cap-main-force-screener',
    name: 'A股主力画像与异动雷达',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '90日主力资金建仓/出货画像扫描与多因子量化初筛',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'PROPOSAL_STAGE_039, MainForceScreener',
  },
  {
    id: 'cap-pattern-tagging',
    name: 'A股股票形态与标签引擎',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '识别破线突破、缩量盘整等经典技术形态并挂载事实标签',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'PROPOSAL_STAGE_037, PatternTaggingEngine',
  },
  {
    id: 'cap-sector-rotation-rrg',
    name: 'A股板块轮动与动量 RRG 罗盘',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: '申万行业与 500+ 概念板块动量 RRG 象限归因分析',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'PROPOSAL_STAGE_038, SectorRotationMatrix',
  },
  {
    id: 'cap-latent-ambush-pivot',
    name: '潜伏雷达与挂单中枢计算',
    domainId: 'quant_indicators',
    domainNameZh: '量化特征与指标计算域',
    domainNameEn: 'QUANT & INDICATORS DOMAIN',
    valueOutcome: 'S1/Fib0.618 关键支撑位挂单推演与左侧潜伏标的推荐',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'PROPOSAL_STAGE_035, LatentAmbushRadar',
  },

  // 3. UI & Presentation
  {
    id: 'cap-view-market-board',
    name: '多资产全景态势看板',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '跨资产市场总览、核心指标横向对比与极速卡片视图',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/market/',
    route: '#/crypto/market-board',
  },
  {
    id: 'cap-view-trend-weather',
    name: '全域趋势气象与时空共振',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '多标的、多周期趋势气象网格热力图与动能共振卡片',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/regime/',
    route: '#/crypto/weather',
  },
  {
    id: 'cap-view-indicators-matrix',
    name: '多周期指标流与动能分析',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '深度指标走势图、量价通道与超买超卖预警大屏',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/indicators/',
    route: '#/crypto/indicators',
  },
  {
    id: 'cap-view-market-feeds',
    name: '主流标的基差与异动雷达',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '期现基差率、资金费率偏离度与跨市场套利异动探测',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/basis/',
    route: '#/crypto/feeds',
  },
  {
    id: 'cap-view-regime-decision',
    name: '态势决策与形态回测视图',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '态势状态转移矩阵、标签归因实验室与基准对照面板',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/regime/',
    route: '#/crypto/regime-decision',
  },
  {
    id: 'cap-view-crypto-brief',
    name: 'AI 决策简报与战术推演档案',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '首席量化策略官宏观推演摘要与多空攻防战术档案',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/brief/',
    route: '#/crypto/brief',
  },
  {
    id: 'cap-view-ashare-screener',
    name: 'A股大盘全域量化初筛中枢',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '5000+ A股多因子极速初筛大屏、智能过滤与下钻分析',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/screener',
  },
  {
    id: 'cap-view-ashare-watchlist',
    name: 'A股核心自选标的极速看板',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '1m 级高频分时异动刷新、Doctor-A 订阅同步与状态指示',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/watchlist',
  },
  {
    id: 'cap-view-ashare-index-radar',
    name: 'A股指数成份归因雷达',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '主要宽基指数（上证/沪深300/科创50）权重归因与热力雷达',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/index-radar',
  },
  {
    id: 'cap-view-ashare-ambush-radar',
    name: 'A股潜伏雷达与挂单中枢',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '潜伏标的精准匹配、关键支撑位推荐与分档挂单策略透镜',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/ambush-radar',
  },
  {
    id: 'cap-view-ashare-ct-matrix',
    name: 'A股全市场 CT 体检中枢',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '全市场涨跌家数比、赚钱效应体检与市场情绪温度计',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/ct-matrix',
  },
  {
    id: 'cap-view-ashare-pattern-tags',
    name: 'A股股票形态标签中枢',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '单股形态全景透析、多周期标签切片与批量形态矩阵',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/pattern-tags',
  },
  {
    id: 'cap-view-ashare-sector-rotation',
    name: 'A股板块轮动矩阵与动量 RRG',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '28个一级行业与热门概念强弱象限切换与动量飞轮呈现',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/sector-rotation',
  },
  {
    id: 'cap-view-ashare-main-profile',
    name: 'A股90日主力画像中枢',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '主力持仓沉淀、筹码密集区与吸筹派发动向深度透析',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/features/ashare/',
    route: '#/ashare/main-profile',
  },
  {
    id: 'cap-infra-bilingual',
    name: '全局双语排版与菜单系统',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '20+ 视图 50%-60% 比例双语标题，全大写无括号，零截断',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'bilingualTitles.ts, SPEC-BILINGUAL-001',
  },
  {
    id: 'cap-infra-viewport',
    name: '全局视口版心与 Daylight 规范',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '统一 .content-viewport 边距，日光鼠尾草主题与 Ghost 边框',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'web/src/index.css, SPEC-GLOBAL-VIEWPORT-001',
  },
  {
    id: 'cap-infra-fsm',
    name: '5态真值机与无假数据呈现',
    domainId: 'ui_presentation',
    domainNameZh: '全景交互与表现呈现域',
    domainNameEn: 'UI & PRESENTATION DOMAIN',
    valueOutcome: '统一呈现 loading/fresh/stale/disconnected/empty，绝无 Mock',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'web/src/shared/, GATE-TRUTHFUL-STATE-MACHINE',
  },

  // 4. Gateway & Governance
  {
    id: 'cap-ingress-bff',
    name: 'Ingress-BFF 鉴权与防重放网关',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '动态 HMAC-SHA256 签名注入、Nonce 防重放防篡改中间件',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ingress-bff, BFF-Contract-251a',
  },
  {
    id: 'cap-demand-mailbox',
    name: '异步需求 Mailbox 协议通道',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '下游系统任务投递、状态拉取与结果回执的异步解耦邮箱',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'MODULES.md#ingress-bff, Stage 042 Mailbox',
    route: '#/system/gateway?tab=mailbox',
  },
  {
    id: 'cap-dr-audit-log',
    name: '灾备审计与对账日志中心',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '容灾状态机审计、Dead-Letter 队列回溯与服务异常排查',
    lifecycle: 'STABLE',
    reuse: 'DEDICATED',
    pointerText: 'web/src/components/DrAuditLogView.tsx',
    route: '#/system/audit',
  },
  {
    id: 'cap-grounding-mesh',
    name: '系统全景接地网格 (Grounding)',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '提供下游智能体即插即用的统一 API 清单与系统拓扑感知',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'web/src/features/grounding/',
    route: '#/system/gateway?tab=grounding',
  },
  {
    id: 'cap-asop-governance',
    name: 'ASOP 协作协议与自动化门禁',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '双 Agent 协同门禁、确定性证据胶囊与纯 Decimal 静态守卫',
    lifecycle: 'STABLE',
    reuse: 'SHARED',
    pointerText: 'docs/governance/ASOP/, ASOP-1.9.6',
  },
  {
    id: 'cap-capability-map',
    name: '全域功能架构树 (本索引)',
    domainId: 'gateway_governance',
    domainNameZh: '网关安全与系统治理底座',
    domainNameEn: 'GATEWAY & GOVERNANCE DOMAIN',
    valueOutcome: '宏观业务能力多维分类、生命周期管理与查重沙盘',
    lifecycle: 'EVOLVING',
    reuse: 'SHARED',
    pointerText: 'docs/context/CAPABILITY_MAP.md',
    route: '#/system/gateway?tab=capabilities',
  },
];
